import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Mail, Building2, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const InviteTeamMemberDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    role_id: "",
    subsidiary_ids: [],
    personal_message: "",
    expiry_days: 7
  });

  useEffect(() => {
    if (open) {
      fetchRoles();
      fetchSubsidiaries();
    }
  }, [open]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true for optimized query
      const response = await axios.get(`${API}/subsidiaries?company_only=true`);
      setSubsidiaries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubsidiaryToggle = (subsidiaryId) => {
    setFormData(prev => ({
      ...prev,
      subsidiary_ids: prev.subsidiary_ids.includes(subsidiaryId)
        ? prev.subsidiary_ids.filter(id => id !== subsidiaryId)
        : [...prev.subsidiary_ids, subsidiaryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.role_id) {
      toast.error("Please fill in email and role");
      return;
    }
    
    if (formData.subsidiary_ids.length === 0) {
      toast.error("Please assign at least one subsidiary");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/team-members/invite`, formData);
      if (response.data.email_sent) {
        toast.success(`Invitation email sent to ${response.data.email}`);
      } else {
        toast.warning("Invitation created, but the email could not be sent. Try resending it.");
      }
      
      // Reset form
      setFormData({
        email: "",
        full_name: "",
        role_id: "",
        subsidiary_ids: [],
        personal_message: "",
        expiry_days: 7
      });
      
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("Error sending invitation:", error);
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setLoading(false);
    }
  };

  const getInvitationPreview = () => {
    const expiryText = formData.expiry_days === 0 
      ? "never expires" 
      : `expires in ${formData.expiry_days} days`;
    
    const subsidiaryText = subsidiaries
      .filter(s => formData.subsidiary_ids.includes(s.id))
      .map(s => s.name)
      .join(", ");
    
    const roleText = roles.find(r => r.id === formData.role_id)?.name || "Not selected";
    
    return `You're inviting ${formData.email || "[email]"} to join as ${roleText} for ${subsidiaryText || "[subsidiaries]"}. This invitation ${expiryText}.`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Invite Team Member
          </DialogTitle>
          <DialogDescription>
            Send an invitation for a new team member to join your company.
            <br />
            <span className="text-xs text-amber-600">Note: Email delivery requires email service integration (e.g., SendGrid, AWS SES)</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Invitation Details */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Invitation Details</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="colleague@example.com"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="full_name">Full Name (Optional)</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  placeholder="Leave blank if you don't know their name yet"
                />
                <p className="text-xs text-slate-500 mt-1">
                  They can update this when they accept the invitation
                </p>
              </div>
              
              <div>
                <Label htmlFor="role_id">Assign Role *</Label>
                <Select value={formData.role_id} onValueChange={(value) => handleChange("role_id", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles
                      .filter(role => role.role_scope !== "platform")
                      .map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Subsidiary Assignment */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Assign to Subsidiaries *
            </h3>
            <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
              {subsidiaries.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No subsidiaries available</p>
              ) : (
                subsidiaries.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`sub-${sub.id}`}
                      checked={formData.subsidiary_ids.includes(sub.id)}
                      onCheckedChange={() => handleSubsidiaryToggle(sub.id)}
                    />
                    <Label htmlFor={`sub-${sub.id}`} className="text-sm cursor-pointer">
                      {sub.name} ({sub.code || sub.location})
                    </Label>
                  </div>
                ))
              )}
            </div>
            {formData.subsidiary_ids.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                Selected: {formData.subsidiary_ids.length} subsidiaries
              </p>
            )}
          </div>

          {/* Personal Message */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Personal Message (Optional)</h3>
            <Textarea
              value={formData.personal_message}
              onChange={(e) => handleChange("personal_message", e.target.value)}
              placeholder="Hi! I'm inviting you to join our team at [Company]. Looking forward to working with you!"
              rows={3}
            />
            <p className="text-xs text-slate-500 mt-1">
              This message will be included in the invitation email
            </p>
          </div>

          {/* Invitation Settings */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Invitation Settings
            </h3>
            <div>
              <Label htmlFor="expiry_days">Invitation Expiry</Label>
              <Select 
                value={formData.expiry_days.toString()} 
                onValueChange={(value) => handleChange("expiry_days", parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days (recommended)</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="0">Never expires</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Invitation Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-sm mb-2 text-blue-900">Invitation Preview</h4>
            <p className="text-sm text-blue-800">
              {getInvitationPreview()}
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteTeamMemberDialog;
